import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Star,
  Footprints,
  MessageCircle,
  Newspaper,
} from "lucide-react";
import { Alignment, Fit, Layout, useRive } from "@rive-app/react-canvas";
import radarAnimation from "@/assets/rive/radar.riv";
import rainbowCloudAnimation from "@/assets/rive/rainbow_cloud.riv";
import martiniAnimation from "@/assets/rive/martini.riv";
import { Expandable } from "../components/ux/Expandable";
import { Chip } from "../components/ux/Chip";
import { ThemeToggle } from "@/components/ux/ThemeToggle";
import imgInstagram from "@/assets/icons/instagram.svg";
import imgTiktok from "@/assets/icons/tiktok.svg";
import { SectionBackground } from "@/components/ux/SectionBackground";
import logo from "@/assets/icons/outclose.svg";
import { HowItWorks } from "../components/HowItWorks";
import Logo from "@/components/Logo";
import { Link } from "wouter";
import { LanguageSelector, useT } from "@/lib/i18n";
import NextDinner from "../components/NextDinner";
import mayaImg from "@/assets/images/maya.webp";
import sapphicPeopleImg from "@/assets/images/lesbian people2.webp";
import alexImg from "@/assets/images/Alex.webp";
import jenniferImg from "@/assets/images/Jennifer.webp";
import timeoutImg from "@/assets/images/timeout.svg";
import themImg from "@/assets/images/them.svg";
import tracking from "@/lib/tracking";
import { onboardingHref } from "@/lib/onboardingUrl";
import AppStoreBadges from "@/components/AppStoreBadges";
import { ManageCookiesLink } from "@/components/consent/ManageCookiesLink";


export default function LandingSapphic() {
  const { t } = useT('landing');

  useEffect(() => {
    tracking.trackPage("Landing Page - Sapphic");
  }, []);

  const { RiveComponent: RadarRive } = useRive({
    src: radarAnimation,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.Center,
    }),
  });

  const { RiveComponent: RainbowRive } = useRive({
    src: rainbowCloudAnimation,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.Center,
    }),
  });

  const { RiveComponent: MartiniRive } = useRive({
    src: martiniAnimation,
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.TopCenter,
    }),
  });

  const scrollToFeatures = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const featuresElement = document.getElementById("features");
    if (featuresElement) {
      featuresElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="relative overflow-hidden">
      <div className="z-10">
        <div className="max-w-7xl mx-auto px-6 flex items-center my-4 justify-between">
          <Logo link="/app" />
          <div className="flex items-center gap-6">
            <nav className="hidden md:flex items-center gap-6">
              <Link
                href="/affiliate"
                className="text-type-950 dark:text-neutral-100 hover:text-primary-500 transition-colors font-medium"
              >
                {t('nav.affiliate')}
              </Link>
              <Link
                href="/locations"
                className="text-type-950 dark:text-neutral-100 hover:text-primary-500 transition-colors font-medium"
              >
                {t('nav.locations')}
              </Link>
            </nav>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <a href="https://app.outclose.com/" className="btn-primary px-4 py-2 w-auto">
                {t('app:common.buttons.login')}
              </a>
            </div>
          </div>
        </div>
        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center flex flex-col items-center justify-center"
          >
            <div className="min-h-screen flex flex-col justify-center items-center">
              {/* Tagline */}
              <div className="glass-bar mb-5 mt-16">
                {t('pages.sapphic.hero.tagline')}
              </div>

              <h1 className="text-5xl md:text-7xl font-semibold mb-4 text-type-950 dark:text-neutral-50 font-Poppins">
                {t('pages.sapphic.hero.titleLine1')}
                <br />
                {t('pages.sapphic.hero.titleLine2')}
              </h1>

              <p className="text-xl font-Poppins mb-6 max-w-2xl mx-auto dark:text-neutral-100">
                {t('pages.sapphic.hero.subtitleLine1')}
                <br />
                {t('pages.sapphic.hero.subtitleLine2')}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <a
                  href="#features"
                  onClick={scrollToFeatures}
                  className="w-full sm:w-auto"
                >
                  <button className="btn glass-btn-secondary px-8 py-2 text-lg w-full">
                    {t('app:common.buttons.learnMore')}
                  </button>
                </a>
                <a
                  className="btn glass-btn-primary px-8 py-2 text-lg w-full sm:w-auto"
                  href="https://app.outclose.com/"
                  target="_self"
                >
                  {t('cta.meetYourPeople')}
                  <Sparkles className="inline-block ml-2 w-5 h-5" />
                </a>
              </div>

              {/* Next Dinner Countdown */}
              <NextDinner avatarImage={sapphicPeopleImg} avatarClassName="h-[86px] w-full mx-auto my-4" />

              {/* App Store Badges */}
              <AppStoreBadges className="justify-center" badgeHeight={40} source="landing_sapphic_hero" />
              <div className="md:flex-[0_1_200px] flex-[0_1_100px] min-h-16" />
            </div>

            {/* Features */}
            <div id="features" className="flex justify-center items-center p-4 mt-16">
              <Chip
                title={t('features.chipTitle')}
                variant="primary"
                icon={<Star strokeWidth={2} />}
              />
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                  rotateY: 0,
                  transformPerspective: 1200,
                }}
                animate={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="card text-center"
              >
                <div className="w-full h-[200px] rounded-xl overflow-hidden">
                  <RadarRive className="w-full h-full fit-cover" />
                </div>
                <h3 className="text-xl font-semibold my-4 dark:text-neutral-100">
                  {t('pages.sapphic.features.matching.title')}
                </h3>
                <p className="text-dark-muted dark:text-neutral-400">
                  {t('pages.sapphic.features.matching.description')}
                </p>
              </motion.div>

              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                  rotateY: 0,
                  transformPerspective: 1200,
                }}
                animate={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="card text-center "
              >
                <div className="w-full h-[200px] rounded-xl overflow-hidden">
                  <RainbowRive />
                </div>
                <h3 className="text-xl font-semibold my-4 dark:text-neutral-100">
                  {t('pages.sapphic.features.community.title')}
                </h3>
                <p className="text-dark-muted dark:text-neutral-400">
                  {t('pages.sapphic.features.community.description')}
                </p>
              </motion.div>

              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                  rotateY: 0,
                  transformPerspective: 1200,
                }}
                animate={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="card text-center rotate-x-[-20deg]"
              >
                <div className="w-full h-[200px] rounded-xl overflow-hidden">
                  <MartiniRive />
                </div>
                <h3 className="text-xl font-semibold my-4 dark:text-neutral-100">
                  {t('features.noHassle.title')}
                </h3>
                <p className="text-dark-muted dark:text-neutral-400">
                  {t('features.noHassle.description')}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* How it works */}
        <SectionBackground className="text-center py-16 justify-center items-center">
          <Chip
            title={t('howItWorks.chipTitle')}
            variant="primary"
            icon={<Footprints strokeWidth={2} />}
          />
          <HowItWorks />
        </SectionBackground>

        {/* Interactive Question */}
        <div className="px-4 sm:px-6 py-16 bg-white dark:bg-neutral-900">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-8 text-type-950 dark:text-neutral-50">
              {t('quiz.question')}
            </h2>
            <div className="flex flex-row gap-4 justify-center items-stretch">
              <a
                href={onboardingHref()}
                className="flex-1 sm:flex-none"
                onClick={() => tracking.landingHeadFirst()}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="card max-w-40 max-h-40 w-full h-full hover:shadow-xl hover:border-primary-500 transition-all cursor-pointer group"
                >
                  <div className="text-5xl mb-3">🧠</div>
                  <div className="text-xl font-semibold text-type-950 dark:text-neutral-100 group-hover:text-primary-500 transition-colors">
                    {t('quiz.headFirst')}
                  </div>
                </motion.button>
              </a>
              <a
                href={onboardingHref()}
                className="flex-1 sm:flex-none"
                onClick={() => tracking.landingHeartFirst()}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="card max-w-40 max-h-40 w-full h-full hover:shadow-xl hover:border-primary-500 transition-all cursor-pointer group"
                >
                  <div className="text-5xl mb-3">❤️</div>
                  <div className="text-xl font-semibold text-type-950 dark:text-neutral-100 group-hover:text-primary-500 transition-colors">
                    {t('quiz.heartFirst')}
                  </div>
                </motion.button>
              </a>
            </div>
            <a
              href={onboardingHref()}
              className="btn-primary inline-block p-4 px-8 mt-6"
            >
              {t('quiz.cta')}
            </a>
          </motion.div>
        </div>
        <div className="bg-white dark:bg-neutral-900 text-center px-4 sm:px-6 py-16 lg:px-8 justify-center items-center">
          <Chip
            title={t('press.chipTitle')}
            variant="primary"
            icon={<Newspaper strokeWidth={2} />}
          />
          <p className="text-center p-8">
            <a
              href="https://www.timeout.com/newyork/news/this-new-site-sets-lgbtq-new-yorkers-up-on-friend-datesso-i-sat-down-for-dinner-with-strangers-111225"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={timeoutImg}
                alt="Timeout"
                className="dark:invert m-4"
                style={{ display: "inline-block", height: 54 }}
              />
            </a>
            <a
              href="https://www.them.us/story/outclose-gayborhood-app-queer-dinners-finding-friends"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={themImg}
                alt="Them"
                className="dark:invert m-4"
                style={{ display: "inline-block", height: 54 }}
              />
            </a>
          </p>
        </div>

        {/* Testimonials */}
        <SectionBackground className="text-center px-4 sm:px-6 py-16 lg:px-8 justify-center items-center">
          <Chip
            title={t('testimonials.chipTitle')}
            variant="primary"
            icon={<MessageCircle strokeWidth={2} />}
          />
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="card p-6 text-left flex flex-col"
            >
              <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                {t('pages.sapphic.testimonials.quote1')}
              </p>
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                {t('pages.sapphic.testimonials.author1')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="card p-6 text-left flex flex-col"
            >
              <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                {t('pages.sapphic.testimonials.quote2')}
              </p>
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                {t('pages.sapphic.testimonials.author2')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="card p-6 text-left flex flex-col"
            >
              <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                {t('pages.sapphic.testimonials.quote3')}
              </p>
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                {t('pages.sapphic.testimonials.author3')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="card p-6 text-left flex flex-col"
            >
              <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                {t('pages.sapphic.testimonials.quote4')}
              </p>
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                {t('pages.sapphic.testimonials.author4')}
              </p>
            </motion.div>
          </div>
        </SectionBackground>

        {/* Meet Your Future Table */}
        <div className="py-8 bg-white dark:bg-neutral-900">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-5xl mx-auto"
          >
            <div className="px-4">
              <h2 className="text-3xl md:text-4xl font-semibold mb-2 text-type-950 dark:text-neutral-50">
                {t('meetTable.title')}
              </h2>
              <p className="text-lg text-dark-muted dark:text-neutral-400 mb-4">
                {t('meetTable.newTablesEveryWeek')}
              </p>
            </div>

            <div className="overflow-x-scroll scrollbar-hidden w-full max-w-7xl mx-auto">
              <div className="flex flex-row gap-8 p-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="card p-0 overflow-hidden min-w-64"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img
                      src={mayaImg}
                      alt="Sam"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-left">
                    <h3 className="text-2xl font-semibold text-type-950 dark:text-neutral-100 mb-2">
                      SAM, 23
                    </h3>
                    <p className="text-dark-muted dark:text-neutral-400">
                      {t('pages.sapphic.people.samBio')}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="card p-0 overflow-hidden min-w-64"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img
                      src={alexImg}
                      alt="Alex"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-left">
                    <h3 className="text-2xl font-semibold text-type-950 dark:text-neutral-100 mb-2">
                      ALEX, 36
                    </h3>
                    <p className="text-dark-muted dark:text-neutral-400">
                      {t('pages.sapphic.people.alexBio')}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="card p-0 overflow-hidden min-w-64"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img
                      src={jenniferImg}
                      alt="Jennifer"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-left">
                    <h3 className="text-2xl font-semibold text-type-950 dark:text-neutral-100 mb-2">
                      JENNIFER, 54
                    </h3>
                    <p className="text-dark-muted dark:text-neutral-400">
                      {t('pages.sapphic.people.jenniferBio')}
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ */}
        <SectionBackground className="text-center px-4 sm:px-6 pt-16 lg:px-8 pb-16">
          <h2 className="text-3xl md:text-4xl font-semibold mb-8 text-type-950 dark:text-neutral-50 font-Poppins">
            {t('faq.chipTitle')}
          </h2>
          <div className="flex flex-col gap-4 max-w-xl mx-auto">
            <Expandable title={t('faq.whatsIncluded.question')}>
              {t('faq.whatsIncluded.answer')}
            </Expandable>
            <Expandable title={t('faq.howAssigned.question')}>
              {t('faq.howAssigned.answer')}
            </Expandable>
            <Expandable title={t('faq.whoCanJoin.question')}>
              {t('pages.sapphic.faq.whoCanJoinAnswer')}
            </Expandable>
            <Expandable title={t('faq.isDating.question')}>
              {t('faq.isDating.answer')}
            </Expandable>
            <Expandable title={t('faq.bringPartner.question')}>
              {t('faq.bringPartner.answer')}
            </Expandable>
            <Expandable title={t('faq.cost.question')}>
              {t('faq.cost.answer')}
            </Expandable>
            <Expandable title={t('faq.whoBehind.question')}>
              {t('pages.sapphic.faq.whoBehindAnswer')}
            </Expandable>
          </div>
        </SectionBackground>

        {/* Footer */}
        <div className="bg-white dark:bg-neutral-900 pb-16 md:mb-0">
          <div className="max-w-7xl mx-auto px-4 py-8 text-center">
            <p className="mb-8">
              <a
                href="https://app.outclose.com/"
                className="btn-primary mx-auto px-8 py-4 w-auto"
              >
                {t('footer.signUpNow')}
              </a>
            </p>
            <AppStoreBadges className="justify-center mb-6" badgeHeight={36} source="landing_sapphic_footer" />
            <img
              src={logo}
              alt="Logo"
              className="w-16 h-16 inline-block dark:invert"
            />
            <p className="text-2xl font-Poppins font-bold mb-2 dark:text-neutral-100">
              Outclose
            </p>
            <div className="flex justify-center items-center gap-2 mb-2">
              <a href="https://instagram.com/outclose.lgbt" target="_blank">
                <img
                  src={imgInstagram}
                  alt="Instagram"
                  className="inline-block ml-2 w-10 h-10 p-2 text-primary-500 dark:invert"
                />
              </a>
              <a href="https://www.tiktok.com/@outclose.lgbt" target="_blank">
                <img
                  src={imgTiktok}
                  alt="TikTok"
                  className="inline-block ml-2 w-10 h-10 p-2 text-primary-500 dark:invert"
                />
              </a>
            </div>
            <span className="dark:text-neutral-300">
              {t('footer.tagline')}
            </span>

            <div className="my-4 flex justify-center">
              <LanguageSelector className="dark:text-neutral-100 dark:border-neutral-600" />
            </div>
            <p className="my-4 text-sm text-center">
              <ManageCookiesLink className="hover:text-primary-500">
                {t('footer.manageCookies')}
              </ManageCookiesLink>
            </p>
            {/* Legal Links */}
            <p className="my-4 text-sm">
              <Link
                href="/terms"
                className="hover:text-primary-500 transition-colors"
              >
                {t('footer.terms')}
              </Link>
              {" | "}
              <Link
                href="/privacy"
                className="hover:text-primary-500 transition-colors"
              >
                {t('footer.privacy')}
              </Link>
              {" | "}
              <Link
                href="/community-guidelines"
                className="hover:text-primary-500 transition-colors"
              >
                {t('footer.communityGuidelines')}
              </Link>
            </p>
            <p className="my-4 text-sm flex items-center justify-center gap-1.5 dark:text-neutral-400">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
              {t('footer.proudlyGayOwned')}
            </p>
            <p className="my-4 text-sm dark:text-neutral-400">
              <a href="https://outclose.com/company" className="hover:text-primary-500 transition-colors">GBH Holdings Inc.</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
